import { TFunction } from "i18next";
import { CheckItem } from "./checkItems";
import { ChecklistGroup } from "./checklistGroups";
import { vehicleIdentification } from "./vehicleIdentification";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import VinDetail from "@/models/VinDetail";
import { Report } from "@/utilities/export/report";

/**
 * The checklist as a report.
 *
 * The vehicle is named in the title and the identification rows: the sheet is filed as the record
 * of an installation, and a file name gets lost. The checks form a flat table with the group
 * repeated on every row, so the sheet can be sorted and filtered without losing which group a
 * check belongs to.
 *
 * The diagnostic id is the last column so the installer can look the value up in the database
 * from the filed sheet.
 *
 * The "Reported" column says where the value came from. Without it a reading from yesterday looks
 * exactly like one reported a second ago, which is the one distinction the checklist exists for.
 */
export function buildChecklistReport(
  device: DeviceWithVehicleInfo | null | undefined,
  groups: ChecklistGroup[],
  t: TFunction,
  vin?: VinDetail,
): Report {
  const notFound = t("liveData.checklist.notFound");
  const title = t("liveData.checklist.title");

  return {
    title: device?.name ? `${title} \u2013 ${device.name}` : title,
    filter: device?.name,
    extra: vehicleIdentification(device, t, notFound, vin).map(field => [field.label, field.value]),
    table: {
      columns: [
        { field: "group", header: t("liveData.checklist.exportGroup") },
        { field: "check", header: t("liveData.checklist.exportCheck") },
        { field: "value", header: t("liveData.value") },
        { field: "reported", header: t("liveData.checklist.exportReported") },
        {
          field: "diagnosticId",
          header: t("liveData.checklist.exportDiagnosticId"),
        },
      ],
      rows: groups.flatMap(group =>
        group.items.map(item => [
          group.title,
          item.label,
          item.value || notFound,
          reportedIn(item, t),
          item.diagnosticId ?? "",
        ]),
      ),
    },
  };
}

function reportedIn(item: CheckItem, t: TFunction): string {
  if (!item.found) {
    return "";
  }
  if (item.mismatch) {
    return t("liveData.checklist.reportedMismatch", { gap: item.mismatch });
  }
  if (item.manual) {
    return t("liveData.checklist.reportedManual");
  }
  if (item.stale) {
    return t("liveData.checklist.reportedOlder");
  }
  if (item.fromFallback) {
    return item.lastReportedAt
      ? t("liveData.checklist.reportedBeforePeriod")
      : t("liveData.checklist.reportedHistory");
  }
  return t("liveData.checklist.reportedLive");
}
